const API_ROUTE_PREFIX = "/api";
const DEFAULT_ORIGIN = "http://localhost";
const ROOT_PATH = "/";
const SETUP_DEVICE_PATH = "/setup-device";

export const POST_LOGIN_REDIRECT_KEY = "postLoginRedirect";

/**
 * OAuth integration initiation endpoints the agent opens in the system
 * browser (see OAUTH_INITIATION_PREFIXES in agent/src/main.ts and
 * startIntegration in shared-ui/src/stores/integration-store.ts). These are
 * the only API paths the backend login redirect legitimately passes along in
 * its `continue` query; all other API paths are dropped so stale or injected
 * redirect targets cannot send a freshly logged-in user to an arbitrary
 * backend endpoint.
 */
const ALLOWED_API_REDIRECT_PATHS = new Set([
  "/api/google/integrate",
  "/api/microsoft/integration",
  "/api/facebook/auth",
]);

function getConfiguredOrigin(): string {
  try {
    return (
      (
        import.meta as ImportMeta & {
          env?: { VITE_PUBLIC_SITE_URL?: string };
        }
      ).env?.VITE_PUBLIC_SITE_URL ?? DEFAULT_ORIGIN
    );
  } catch {
    return DEFAULT_ORIGIN;
  }
}

function getCurrentOrigin(): string {
  try {
    return globalThis.location?.origin ?? getConfiguredOrigin();
  } catch {
    return getConfiguredOrigin();
  }
}

function getRedirectStorage(storage?: Storage): Storage | null {
  if (storage) {
    return storage;
  }

  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * Backend API targets (e.g. OAuth initiation endpoints reached via the
 * `continue` query on the login redirect) require a full page load instead of
 * a router navigation.
 */
export function isApiRedirectTarget(target: string): boolean {
  const pathname = target.split(/[?#]/, 1)[0] ?? "";
  return (
    pathname === API_ROUTE_PREFIX || pathname.startsWith(`${API_ROUTE_PREFIX}/`)
  );
}

export function normalizePostLoginRedirectTarget(
  target: string,
  origin: string = getCurrentOrigin(),
): string | null {
  let url: URL;
  try {
    url = new URL(target, origin);
  } catch {
    return null;
  }

  if (url.origin !== origin || url.pathname === ROOT_PATH) {
    return null;
  }

  // Setup continues to this target when it finishes, so it can't be setup.
  if (
    url.pathname === SETUP_DEVICE_PATH ||
    url.pathname.startsWith(`${SETUP_DEVICE_PATH}/`)
  ) {
    return null;
  }

  if (
    isApiRedirectTarget(url.pathname) &&
    !ALLOWED_API_REDIRECT_PATHS.has(url.pathname)
  ) {
    return null;
  }

  return `${url.pathname}${url.search}${url.hash}`;
}

export function rememberPostLoginRedirect(
  target: string,
  storage?: Storage,
  origin: string = getCurrentOrigin(),
): void {
  const normalizedTarget = normalizePostLoginRedirectTarget(target, origin);
  if (!normalizedTarget) {
    return;
  }

  const redirectStorage = getRedirectStorage(storage);
  if (!redirectStorage) {
    return;
  }

  try {
    redirectStorage.setItem(POST_LOGIN_REDIRECT_KEY, normalizedTarget);
  } catch {
    // Storage may be unavailable or blocked by browser privacy settings.
  }
}

export function consumePostLoginRedirect(
  storage?: Storage,
  origin: string = getCurrentOrigin(),
): string | null {
  const redirectStorage = getRedirectStorage(storage);
  if (!redirectStorage) {
    return null;
  }

  let target: string | null;
  try {
    target = redirectStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    if (!target) {
      return null;
    }

    redirectStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  } catch {
    return null;
  }

  return normalizePostLoginRedirectTarget(target, origin);
}
